import classNames from 'classnames';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import Card from '../../../components/atoms/card';
import Actions from '../../../components/molecules/actions';
import MVCHeader from '../../../components/organisms/mvc-header';
import Pagination, {
	PaginationProps,
} from '../../../components/organisms/pagination';
import Table, { TableColumn } from '../../../components/organisms/table';
import { ModalProps } from '../../../components/template/mvc-modal';
import MaintenanceService, {
	IMaintenance,
} from '../../../services/Maintenance.service';
import { dateObjToStr } from '../../../utils/date';
import Text from '../../../components/atoms/text';

const initialState: IMaintenance = {
	yard: {},
	description: '',
	date: new Date(),
	hh: '',
	voltage: '',
	size: '',
	manufactory: '',
	container: '',
	block: '',
	batch: '',
	place: '',
	imgFiles: [],
	materials: [],
};

const Maintenance = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const modalTitles = {
		create: t('maintenance.new'),
		edit: t('maintenance.edit'),
		view: t('maintenance.view'),
	};

	const [data, setData] = useState<IMaintenance[]>([]);

	const [paginationOptions, setPaginationOptions] = useState<PaginationProps>(
		{
			pageCurrent: 1,
			pageSize: 10,
			totalItems: 0,
			totalPages: 1,
		}
	);

	const [modalProps, setModalProps] = useState<ModalProps<IMaintenance>>({
		data: initialState,
		title: modalTitles.view,
		mode: 'view',
		isModalVisible: false,
		onClose: (reload?: boolean) => {
			setModalProps((prev) => ({
				...prev,
				isModalVisible: false,
				data: initialState,
			}));
			if (reload) fetchData();
		},
	});

	const fetchData = useCallback(async () => {
		const response = await MaintenanceService.getAll({
			pageCurrent: paginationOptions.pageCurrent,
			pageSize: paginationOptions.pageSize,
		});
		if (response) {
			setData(response.data);
			setPaginationOptions((prev) => ({
				...prev,
				totalItems: response.totalItems,
				totalPages: response.totalPages,
			}));
		}
	}, [paginationOptions.pageCurrent, paginationOptions.pageSize]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const onPaginationUpdate = (pageSize: number, pageCurrent: number) => {
		setPaginationOptions((prev) => ({ ...prev, pageSize, pageCurrent }));
	};

	const onActionsViewClick = (data: IMaintenance) =>
		setModalProps((prev) => ({
			...prev,
			title: modalTitles.view,
			mode: 'view',
			isModalVisible: true,
			data,
		}));

	const onActionsEditClick = (data: IMaintenance) => {
		navigate('/maintenance/new', { state: data });
	};

	const columns: TableColumn<IMaintenance>[] = [
		{
			key: 'date',
			label: t('date.title'),
			children: (row) => (
				<Text className="text-xs leading-none">
					{dateObjToStr(row.date)}
				</Text>
			),
		},
		{ key: 'hh', label: t('maintenance.hh') },
		{ key: 'voltage', label: t('maintenance.voltage') },
		{
			key: 'size',
			label: t('misc.size'),
		},
		{
			key: 'container',
			label: t('port_management.container'),
		},
		{
			key: 'actions',
			label: t('misc.actions'),
			children: (row) => (
				<Actions
					onViewClick={() => onActionsViewClick(row)}
					onEditClick={() => onActionsEditClick(row)}
				/>
			),
		},
	];

	const onCreateClick = () => navigate('/maintenance/new');

	return (
		<>
			{location.pathname === '/maintenance' ? (
				<Card className="flex-grow grid grid-rows-[auto_1fr_auto] p-4 gap-y-4">
					<MVCHeader
						title={t('maintenance.list')}
						onCreateText={t('maintenance.new.title')}
						onCreateClick={onCreateClick}
					/>

					<div className="overflow-x-auto w-full">
						<Table
							data={data}
							columns={columns}
							className={classNames(
								'grid-cols-[1fr_1fr_1fr_1fr_1fr_auto]'
							)}
						/>
					</div>

					<Pagination
						{...paginationOptions}
						onUpdate={onPaginationUpdate}
					/>
				</Card>
			) : (
				<Outlet />
			)}
		</>
	);
};

export default Maintenance;
